import React from 'react';
import Avatar from '../assets/images/users/avt1.png'
import { BiListUl, BiCalendarPlus, BiLogOut, BiHome, BiLayout } from "react-icons/bi";
import { useNavigate, useLocation } from 'react-router-dom';
import {
    useMsal,
    MsalAuthenticationTemplate,
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { useLang } from '../hooks/useLabels';
import { UserRole } from '../callMsGroup';
import { getRealRole } from '../utils/roleOverride';

const NAV = {
    home:      'Home',
    myevents:  'My Events',
    myforms:   'My Forms',
    admin:     'Admin',
    templates: 'Forms',
    logout:    'Logout',
    menu:      'Menu',
};

interface UserTopbarProps {
    role?: UserRole;
    isAdmin?: boolean;       // legacy — kept for backward compat
    photoData?: string;
    token?: string;
    collapsible?: boolean;
    open?: boolean;
    showLang?: boolean;
    triggerSearch?: (kw: string) => void;
}

export default function UserTopbar(props: UserTopbarProps) {
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === '/' || location.pathname === '/home';
    const isMyEvents = location.pathname === '/myevents';
    const [lang, setLang] = useLang();
    const nav = (key: keyof typeof NAV) => NAV[key];

    const realRole = getRealRole();
    const role: UserRole = realRole === 'superadmin'
        ? 'superadmin'
        : props.role ?? realRole ?? (props.isAdmin ? 'admin' : 'owner');
    const isSuperAdmin = role === 'superadmin';
    const isAdminRole = role === 'admin';
    const canSeeAdminMenu = isAdminRole || isSuperAdmin;

    const handleLogout = () => {
        instance.logoutRedirect({ postLogoutRedirectUri: "/" });
    };

    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
            <aside className={`admin-sidebar${props.collapsible ? (props.open ? ' admin-sidebar--open' : ' admin-sidebar--collapsed') : ''}`}>
                {/* Logo */}
                <div className="sidebar-brand" onClick={() => navigate(isSuperAdmin ? '/forms' : '/myevents')} style={{ cursor: 'pointer' }}>
                    <span className="sidebar-brand-text">C&amp;S EVENTS PPV</span>
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                    <div className="sidebar-nav-label">{nav('menu')}</div>
                    {!isSuperAdmin && (
                        <>
                            <button
                                className={`sidebar-nav-item${isHome ? ' active' : ''}`}
                                onClick={() => navigate('/')}
                            >
                                <BiHome size={18} /> <span>{nav('home')}</span>
                            </button>
                            <button
                                className={`sidebar-nav-item${isMyEvents ? ' active' : ''}`}
                                onClick={() => navigate('/myevents')}
                            >
                                <BiListUl size={18} /> <span>{nav('myevents')}</span>
                            </button>
                        </>
                    )}
                    {canSeeAdminMenu && (
                        <>
                            <button className="sidebar-nav-item" onClick={() => navigate('/admin')}>
                                <BiCalendarPlus size={18} /> <span>{nav('admin')}</span>
                            </button>
                            <button className="sidebar-nav-item" onClick={() => navigate('/forms')}>
                                <BiLayout size={18} /> <span>{nav('templates')}</span>
                            </button>
                        </>
                    )}
                </nav>

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Profile + Logout */}
                <div className="sidebar-footer">
                    <div className="sidebar-profile">
                        <img
                            src={props.photoData || Avatar}
                            className="sidebar-avatar"
                            alt="avatar"
                        />
                        <div className="sidebar-profile-info">
                            <div className="sidebar-profile-name">{accounts[0]?.name}</div>
                        </div>
                    </div>
                    {props.showLang && (
                    <div className="sidebar-lang-toggle">
                        <button
                            className={`sidebar-lang-btn${lang === 'en' ? ' active' : ''}`}
                            onClick={() => setLang('en')}
                        >EN</button>
                        <span className="sidebar-lang-sep">|</span>
                        <button
                            className={`sidebar-lang-btn${lang === 'vn' ? ' active' : ''}`}
                            onClick={() => setLang('vn')}
                        >VN</button>
                    </div>
                    )}
                    <button className="sidebar-logout-btn" onClick={handleLogout}>
                        <BiLogOut size={16} /> {nav('logout')}
                    </button>
                </div>
            </aside>
        </MsalAuthenticationTemplate>
    );
}
